// src/components/Hotel/DestinationInput.jsx
import React, { useState, useEffect } from "react";
import { getCityId, handleCitySelection } from "../../utils/Hote";
import LocationOnIcon from '@mui/icons-material/LocationOn';

export const DestinationInput = ({
  translation,
  destinationValue,
  destinationSelectedValue,
  setDestinationValue,
  setDestinationSelectedValue,
  setCityId,
  setCityLat,
  setCityLng,
  readOnly = false,
  onSelect,
  onFocus,
  onBlur,
  hasError = false,
  onDropdownToggle = null
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  // detect mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Call onDropdownToggle when showSuggestions changes
  useEffect(() => {
    if (onDropdownToggle) {
      // Trigger blur immediately when input is focused (showSuggestions is true)
      // regardless of search results or typing
      onDropdownToggle(showSuggestions);
    }
  }, [showSuggestions, onDropdownToggle]);

  // Search for cities when input changes
  useEffect(() => {
    const searchCities = async () => {
      if (!destinationValue?.trim() || destinationValue.length < 3) {
        setCities([]);
        return;
      }

      setLoading(true);
      try {
        let searchTerm = destinationValue;
        let isAlbanianMapping = false;
        let mappingConfig = null;
        
        // Add autocomplete mapping for common Albanian/local pronunciations
        const lowerValue = destinationValue.toLowerCase();
        
        if (lowerValue.startsWith('halki') || lowerValue.startsWith('hallki')) {
          searchTerm = 'Chalkidiki';
          isAlbanianMapping = true;
          mappingConfig = {
            type: 'chalkidiki',
            searchName: 'Chalkidiki',
            displayName: 'Hallkidiki',
            targetCityId: '10386'
          };
        } else if (lowerValue.startsWith('prish')) {
          searchTerm = 'Pristina';
          isAlbanianMapping = true;
          mappingConfig = {
            type: 'prishtina', 
            searchName: 'Pristina',
            displayName: 'Prishtina',
            targetCityId: '61825'
          };
        }
        
        
        const results = await getCityId(searchTerm);
        
        // Filter out cities with hotelCount 0 and map Albanian names
        const filteredResults = results?.filter(city => city.hotelCount > 0)?.map(city => {
          // Map back to Albanian names for display if this is a mapped search
          if (isAlbanianMapping && mappingConfig) {
            // Check if this city matches our mapping target
            const cityName = city.city?.name || city.name;
            
            if (mappingConfig.type === 'chalkidiki' && cityName === mappingConfig.searchName) {
              return {
                ...city,
                name: mappingConfig.displayName, // Display as "Hallkidiki"
                originalName: cityName, // Keep original "Chalkidiki" for API calls
                city: {
                  ...city.city,
                  name: mappingConfig.displayName,
                  id: mappingConfig.targetCityId
                },
                id: mappingConfig.targetCityId,
                cityId: mappingConfig.targetCityId
              };
            } else if (mappingConfig.type === 'prishtina' && cityName === mappingConfig.searchName) {
              return {
                ...city,
                name: mappingConfig.displayName, // Display as "Prishtina"
                originalName: cityName, // Keep original "Pristina" for API calls
                city: {
                  ...city.city,
                  name: mappingConfig.displayName,
                  id: mappingConfig.targetCityId
                },
                id: mappingConfig.targetCityId,
                cityId: mappingConfig.targetCityId
              };
            }
          }
          
          // Return original city data for non-mapped results
          return {
            ...city,
            name: city.city?.name || city.name,
            originalName: city.city?.name || city.name,
            id: city.city?.id || city.id,
            cityId: city.city?.id || city.id
          };
        }) || [];
        
        // If this was an Albanian mapping search and we found results, prioritize the mapped result
        if (isAlbanianMapping && mappingConfig && filteredResults.length > 0) {
          const mappedResult = filteredResults.find(city => 
            city.name === mappingConfig.displayName
          );
          
          if (mappedResult) {
            // Put the mapped result first
            const otherResults = filteredResults.filter(city => 
              city.name !== mappingConfig.displayName
            );
            setCities([mappedResult, ...otherResults]);
          } else {
            setCities(filteredResults);
          }
        } else {
          setCities(filteredResults);
        }
        
      } catch (error) {
        console.error("Error searching cities:", error);
        setCities([]);
      } finally {
        setLoading(false);
      }
    };

    searchCities();
  }, [destinationValue]);

  const handleSelection = async (city) => {
    
    const selectedCity = {
      name: city.name, // Albanian display name (Hallkidiki, Prishtina) or regular name
      originalName: city.originalName || city.name, // Original API name (Chalkidiki, Pristina) for API calls
      country_code: city.country?.id || city.country_code,
      id: city.cityId || city.city?.id || city.id,
      geolocation: city.geolocation,
      cityId: city.cityId || city.city?.id || city.id,
      latitude: city.geolocation?.latitude,
      longitude: city.geolocation?.longitude,
      isAlbanianMapping: !!(city.originalName && city.originalName !== city.name)
    };
    
    
    setDestinationSelectedValue(selectedCity);
    setCityId(selectedCity.cityId);
    if (city.geolocation) {
      setCityLat(city.geolocation.latitude);
      setCityLng(city.geolocation.longitude);
    }
    setShowSuggestions(false);
    setDestinationValue(selectedCity.name); // Display Albanian name

    if (onSelect) onSelect(selectedCity);
  };

  const handleInputFocus = (e) => {
    if (!readOnly) {
      setShowSuggestions(true); // This will trigger the blur immediately
    }
    if (onFocus) onFocus(e);
  };

  const handleInputBlur = (e) => {
    setTimeout(() => setShowSuggestions(false), 300);
    if (onBlur) onBlur(e);
  };

  const handleInputChange = (e) => {
    if (destinationSelectedValue) setDestinationSelectedValue(null);
    setDestinationValue(e.target.value);
    if (!readOnly) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="position-relative" style={{ width: "100%" }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: "#f8f9fa",
        borderRadius: '8px',
        height: '56px',
        border: hasError ? '1px solid #dc3545' : 'none',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          paddingLeft: '15px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <LocationOnIcon style={{ color: hasError ? '#dc3545' : '#003663', fontSize: 20 }} />
        </div>
        <input
          id="destination-input"
          type="search"
          className={`form-control destination-input ${hasError ? 'error-placeholder' : ''}`}
          value={destinationSelectedValue?.name || destinationValue}
          onChange={handleInputChange}
          placeholder={translation("destination")}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          readOnly={readOnly}
          style={{
            padding: "12px 16px",
            paddingLeft: "8px",
            border: 'none',
            background: "transparent",
            cursor: readOnly ? "default" : "pointer",
            height: '56px',
            fontSize: '18px',
            fontWeight: '500',
            color: hasError ? '#dc3545' : '#003663',
            '&::placeholder': {
              color: hasError ? '#dc3545' : '#6c757d',
              opacity: 1
            }
          }}
        />
      </div>

      {(showSuggestions || loading) && (
        <ul
          className="list-group position-absolute w-100 mt-1 bg-white shadow"
          style={{
            zIndex: 9999,
            maxHeight: isMobile ? "85vh" : "300px",
            overflowY: "auto",
            border: "1px solid #ddd",
            borderRadius: "4px"
          }}
        >
          {loading ? (
            <li className="list-group-item">{translation?.searching || "Searching..."}</li>
          ) : destinationValue?.length < 3 ? (
            <li className="list-group-item">{translation?.write_at_least_3_characters || "Write at least 3 characters"}</li>
          ) : cities.length > 0 ? (
            cities.map((city) => {
              return (
                <li
                  key={city.id || city.city?.id}
                  className="list-group-item list-group-item-action"
                  onClick={() => handleSelection(city)}
                  style={{ cursor: "pointer" }}
                >
                  {city.name || city.city?.name} ({city.country?.name || city.country_name})
                </li>
              );
            })
          ) : (
            <li className="list-group-item">{translation?.no_results_found || "No results found"}</li>
          )}
        </ul>
      )}
    </div>
  );
};
